import React from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Grid, Link, Typography } from '@material-ui/core';
import clsx from 'clsx';
import ExpandMore from '@material-ui/icons/ExpandMore';
import { parse, stringify } from 'query-string';

// CONSTANTS
import {
  ROUTE_DISCOVER_APP,
  ROUTE_DISCOVER_SEARCH,
  ROUTE_PROFILE_SUBSCRIPTION_DETAIL,
  ROUTE_SUBSCRIPTION,
} from '@/constants/routes';
import { appsListStatuses } from '@/constants/app';

// UTILITY
import { externalLink, getExternalUrlParams } from '@/utility/analytics';
import getLink from '@/utility/routes';
import { updateUrl } from '@/utility/common';

// STORE
import { AppDetailsActions } from '@/store/actions';

// HOOKS
import useActions from '@/hooks/useActions';

// HOC
import CheckAuth from '@/hoc/CheckAuth';

// COMPONENTS
import RouterLink from '@/components/Link';
import SearchCardManager from '@/components/Cards/SearchCardManager';
import MBIcon from '@/components/Icons/MBIcon';

// HELP
import { Container } from '../styles';

const AppsSection = ({ data, onUpdateStatus, className, analyticsLocation, ...props }) => {
  const [t] = useTranslation();
  const location = useLocation();

  const { unique_id, anonymousId, isSimpleView, isIframe, partner, searchLoadings } = useSelector(
    ({ auth, app, partner: partnerStore, search }) => ({
      unique_id: auth.unique_id,
      anonymousId: auth.anonymousId,
      isSimpleView: app.isSimpleView,
      isIframe: app.isIframe,
      partner: partnerStore.data,
      searchLoadings: search.loadings,
    }),
  );

  const [appListRemoveStatus, appListUpdateStatus] = useActions([
    requestData => AppDetailsActions.appListRemoveStatus.action(requestData),
    requestData => AppDetailsActions.appListUpdateStatus.action(requestData),
  ]);

  const partnerSettings = partner?.settings || {};

  const externalUrlParams = getExternalUrlParams({ unique_id: unique_id || anonymousId });

  function handleUpdateAppListStatus(appData) {
    const appAddedToList = appData.list_status === appsListStatuses.have;
    let requestData;

    if (appAddedToList) {
      requestData = {
        app_id: appData.id,
        callback: () => onUpdateStatus(appData, null),
      };

      appListRemoveStatus(requestData);
    } else {
      requestData = {
        data: {
          apps: [{ app_id: appData.id, status: appsListStatuses.have }],
        },
        callback() {
          onUpdateStatus(appData, appsListStatuses.have);
        },
      };

      appListUpdateStatus(requestData);
    }
  }

  function handleVisitSite(appData, href) {
    const properties = {
      location: 'app hover',
      href,
      app_id: appData.id,
      type: 'app',
      service_name: appData.name,
      is_affiliate: !!appData.is_affiliate,
      partner: partner?.partner_slug,
      partner_id: partner?.partner_id,
    };

    externalLink(properties);
  }

  function getActions(appData) {
    const link = getLink(ROUTE_DISCOVER_APP, { slug: appData.unique_slug });
    const href = updateUrl(appData.url, externalUrlParams);
    const list_status = appData.list_status;
    const appAddedToList = list_status === appsListStatuses.have;
    const isSubscribed = appData.user_subscribed;

    let useSubscription = true;
    let useAuthorizing = true;

    if (partner.slug) {
      useAuthorizing = !!partnerSettings.use_authorizing;
      useSubscription = !!partnerSettings.use_authorizing && !!partnerSettings.use_subscription;
    }

    const displayButtonsAppsList = useAuthorizing && !isSubscribed && !isSimpleView;

    const subscriptionIsAvailable = useSubscription && appData.has_subscription && !isSimpleView;
    const isUnsubscribedInIframe = isIframe && !isSubscribed;

    const actions = [
      {
        title: t('buttons.learnMore'),
        onClick: () => {},
        component: RouterLink,
        to: link,
        location: analyticsLocation,
      },
      {
        title: data.external_button_text ?? t('buttons.visitSite'),
        onClick: () => handleVisitSite(appData, href),
        rel: 'noreferrer',
        target: '_blank',
        component: 'a',
        href,
      },
    ];

    if (displayButtonsAppsList) {
      actions.push({
        title: appAddedToList ? t('buttons.removeFromMyApps') : t('buttons.addToMyApps'),
        onClick: () => handleUpdateAppListStatus(appData),
        hoc: CheckAuth,
        disabled: !!searchLoadings.statusesLoadings[appData.id],
      });
    }

    if (subscriptionIsAvailable) {
      actions.unshift({
        icon: <MBIcon color="inherit" fontSize="inherit" />,
        component: RouterLink,
        to: getLink(isSubscribed ? ROUTE_PROFILE_SUBSCRIPTION_DETAIL : ROUTE_SUBSCRIPTION, {
          slug: appData.unique_slug,
        }),
        ...(isUnsubscribedInIframe ? { target: '_blank' } : {}),
        withoutIframe: isUnsubscribedInIframe,
        location: analyticsLocation,
        title: isSubscribed ? t('buttons.manage') : t('buttons.subscribe'),
        hoc: CheckAuth,
        hocProps: {
          authComponentProps: {
            type: 'subscription',
          },
        },
      });
    }

    return actions;
  }

  function getSeeAllLink() {
    const locaitonQueryParams = parse(location.search, {
      arrayFormat: 'comma',
      skipEmptyString: true,
      parseNumbers: true,
    });

    const params = {
      search: locaitonQueryParams.search || '',
      type: ['paid', 'free'],
    };

    const search = stringify(params, {
      arrayFormat: 'comma',
      skipEmptyString: true,
    });

    return `${ROUTE_DISCOVER_SEARCH}?${search}`;
  }

  function getGenresLink() {
    const genreIds = data.genres.map(genre => genre.slug);
    const uniqueGenreIds = [...new Set(genreIds)];
    const params = {
      genres: uniqueGenreIds,
      type: ['paid', 'free'],
    };

    const search = stringify(params, {
      arrayFormat: 'comma',
      skipEmptyString: true,
    });

    return `${ROUTE_DISCOVER_SEARCH}?${search}`;
  }

  function getGenresTitle() {
    const genreNames = data.genres.map(genre => genre.name);
    const uniqueGenreNames = [...new Set(genreNames)];

    return (
      <Trans
        i18nKey={`common.${
          uniqueGenreNames.length === 1 ? 'seeEntityInCategory' : 'seeEntityInString'
        }`}
        values={{
          string: uniqueGenreNames.map(genre => `<b>${genre}</b>`).join(' | '),
          entity: t(`common.apps`),
        }}
        components={{
          b: <Typography variant="h6" component="b" color="inherit" />,
        }}
      />
    );
  }

  const seeAllData = React.useMemo(() => {
    if (!data.show_more) return null;

    const link = getSeeAllLink();
    const title = t('common.showAllApps');

    return { link, title };
  }, [data.show_more]);

  const genresData = React.useMemo(() => {
    if (!data.genres.length) return null;

    const link = getGenresLink();
    const title = getGenresTitle();

    return { link, title };
  }, [data.genres]);

  return (
    <Container {...props} className={clsx('section-manager', 'section-manager-apps', className)}>
      <Typography className="section-manager__title" variant="h3">
        {t('common.apps')}
      </Typography>
      <Grid container spacing={2}>
        {genresData && (
          <Grid item xs={12}>
            <Link
              className="section-manager__link-genres"
              component={RouterLink}
              to={genresData.link}
              color="textPrimary"
            >
              {genresData.title}
            </Link>
          </Grid>
        )}
        {data.data.map((item, idx) => (
          <Grid item xs={12} key={idx}>
            <SearchCardManager
              type="app"
              data={item}
              actions={getActions(item)}
              analyticsLocation={analyticsLocation}
            />
          </Grid>
        ))}
      </Grid>
      {seeAllData && (
        <div className="section-manager__link">
          <Link component={RouterLink} to={seeAllData.link} color="inherit">
            {seeAllData.title}{' '}
            <ExpandMore className="section-manager__link-icon" fontSize="inherit" color="inherit" />
          </Link>
        </div>
      )}
    </Container>
  );
};

AppsSection.defaultProps = {
  className: '',
  onUpdateStatus: () => {},
  analyticsLocation: '',
};

AppsSection.propTypes = {
  data: PropTypes.object.isRequired,
  className: PropTypes.string,
  onUpdateStatus: PropTypes.func,
  analyticsLocation: PropTypes.string,
};

export default React.memo(AppsSection);
